import { RenderEngine } from "./RenderEngine";
import { AudioListener, Object3D, Audio, PositionalAudio } from "three";

export enum AudioSourceType {
  Global,
  Positional
}

export class AudioSource {
  private _object3D:Object3D
  private _volume:number = 1;
  private _muteTokens:any[] = [];

  public get object3D():Object3D { return this._object3D; }
  public get audioElement():HTMLAudioElement { return this._audioElement; }

  public get volume():number {
    return this._volume;
  }

  public set volume(value:number) {
    this._volume = value;
    this._audioElement.volume = this.volume;
  }

  public get muted():boolean {
    return this._muteTokens.length > 0;
  }

  public constructor(
    private _audioElement:HTMLAudioElement,
    listener:AudioListener,
    type:AudioSourceType
  ) {
    switch (type) {
      case AudioSourceType.Positional: {
        // const audio = new PositionalAudio(listener);
        // audio.setMediaElementSource(audioElement);
        // audio.setRefDistance(1); // TODO
        // audio.setDirectionalCone(360, 0, 0); // TODO
        const audio = new Object3D();

        this._object3D = audio;
        break;
      }
      case AudioSourceType.Global: {
        // const audio = new Audio(listener);
        // audio.setMediaElementSource(audioElement);
        const audio = new Object3D();

        this._object3D = audio;
        break;
      }
    }
  }

  public reset():void {
    this._audioElement.pause();
    this._audioElement.currentTime = 0; 
  }

  public play(loop = false, reset = true):void {
    if (reset) {
      this.reset();
    }
    this._audioElement.loop = loop;
    this._audioElement.play();
  }

  public pause():void {
    this._audioElement.pause();
  }

  public acquireMuteToken():any {
    const token = {};

    this._muteTokens.push(token);
    this._audioElement.muted = this.muted;
    return token;
  }

  public releaseMuteToken(token:any):void {
    this._muteTokens = this._muteTokens.filter(tk => tk != token);
    this._audioElement.muted = this.muted;
  }
}

export class AudioController {
  private audioListener:AudioListener;
  private renderEngine:RenderEngine;
  private _initialized:boolean = false;
  private audioSources:AudioSource[] = [];
  private _muted:boolean = true;
  private muteTokens:{source:AudioSource, token:any}[] = [];

  public get initialized():boolean { return this._initialized; }

  public get muted():boolean { return this._muted; }

  public set muted(value:boolean) {
    if (this._muted != value) {
      this._muted = value;
      if (value) {
        this.muteTokens.length = 0; // to be safe

        for (const source of this.audioSources) {
          this.muteTokens.push({source, token: source.acquireMuteToken()});
        }
      } else {
        for (const muteToken of this.muteTokens) {
          muteToken.source.releaseMuteToken(muteToken.token);
        }

        this.muteTokens.length = 0;
      }
    }
  }

  public constructor() {
    this.audioListener = new AudioListener();

    (window as any)["audioController"] = this;
  }

  public initialize(renderEngine:RenderEngine) {
    // TODO: use XR camera in XR mode  
    // renderEngine.camera.add(this.audioListener);
    this.renderEngine = renderEngine;
    this._initialized = true;
  }

  public instantiateGlobalAudioSource(src:string):AudioSource {
    const audioElement = this.instantiateAudioElement(src);
    const audioSource = new AudioSource(audioElement, this.audioListener, AudioSourceType.Global);

    // If an audio source is created while audio is muted,
    // make sure that we request a mute token for it right away.
    if (this.muted) {
      this.muteTokens.push({source: audioSource, token: audioSource.acquireMuteToken()});
    }

    // TODO: what if we just don't,,,,
    this.renderEngine.scene.add(audioSource.object3D);
  
    this.audioSources.push(audioSource);
    return audioSource;
  }

  public instantiatePositionalAudioSource(src:string, parentName:string):AudioSource {
    const parent = this.renderEngine.scene.getObjectByName(parentName);

    if (!parent) {
      console.error(`failed to find parent for audio source: '${parentName}'`);
      return;
    }

    const audioElement = this.instantiateAudioElement(src);
    const audioSource = new AudioSource(audioElement, this.audioListener, AudioSourceType.Positional);

    // If an audio source is created while audio is muted,
    // make sure that we request a mute token for it right away.
    if (this.muted) {
      this.muteTokens.push({source: audioSource, token: audioSource.acquireMuteToken()});
    }

    parent.add(audioSource.object3D);
    this.audioSources.push(audioSource);
    return audioSource;
  }

  public releaseAudioSource(audioSource:AudioSource):void {
    audioSource.reset();
    
    if (audioSource.object3D && audioSource.object3D.parent) {
      audioSource.object3D.parent.remove(audioSource.object3D);
    }

    this.audioSources = this.audioSources.filter(a => a != audioSource);
    this.muteTokens = this.muteTokens.filter(mt => mt.source != audioSource);
  }

  private instantiateAudioElement(src:string):HTMLAudioElement {
    const audio = document.createElement('audio');
    const source = document.createElement('source');

    source.src = src;
    if (src.endsWith('.wav')) {
      source.type = 'audio/wav';
    } else {
      source.type = 'audio/mpeg';
    }
    audio.appendChild(source);
    // audio.preload = "auto"; // TODO: think about this

    return audio;
  }
}
